import color from 'chalk';
import { DEFAULT_CLI_ACCESS_TOKEN, MACHINE_ID_HEADER, USERNAME_HEADER } from '../../constants';
import * as u from '../blocks/ts/url';
import type { ParseOptions, RegistryProvider } from './types';

/** Valid paths
 *
 *  `(https|http)://example.com`
 */
export const http: RegistryProvider = {
	name: 'http',

	matches: (url) => {
		// if parsing is a success then it's a match
		try {
			new URL(url);

			return true;
		} catch {
			return false;
		}
	},

	parse: (url, opts) => {
		const parsed = parseUrl(url, opts);

		return {
			url: parsed.url,
			specifier: parsed.specifier
		};
	},

	baseUrl: (url) => {
		const { url: u } = parseUrl(url, { fullyQualified: false });

		return new URL(u).origin;
	},

	state: async (url) => {
		const { url: normalizedUrl } = parseUrl(url, { fullyQualified: false });

		return {
			url: normalizedUrl,
			provider: http
		};
	},

	resolveRaw: async (state, resourcePath) => {
		// essentially assert that we are using the correct state
		if (state.provider.name !== http.name) {
			throw new Error(
				`You passed the incorrect state object (${state.provider.name}) to the ${http.name} provider.`
			);
		}

		return new URL(resourcePath, state.url);
	},

	authHeader: (token) => ['Authorization', `Bearer ${token}`],

	extraTokenHeaders: ({ userName, machineId }) => {
		return [
			[USERNAME_HEADER, userName],
			[MACHINE_ID_HEADER, machineId]
		];
	},

	headers: () => {
		if (!DEFAULT_CLI_ACCESS_TOKEN) {
			throw new Error('No CLI_ACCESS_TOKEN found for http provider');
		}

		return new Headers({
			'user-agent': 'ngm-dev-cli',
			'x-cli-token': DEFAULT_CLI_ACCESS_TOKEN
		});
	},

	formatFetchError: (state, filePath, error) => {
		return `There was an error fetching ${color.bold(new URL(filePath, state.url).toString())}
	
${color.bold(error)}`;
	}
};

const parseUrl = (
	url: string,
	{ fullyQualified }: ParseOptions
): {
	url: string;
	specifier?: string;
} => {
	const parsedUrl = new URL(url);

	let segments = parsedUrl.pathname.split('/');

	let specifier: string | undefined;

	if (fullyQualified) {
		specifier = segments.slice(segments.length - 2).join('/');

		segments = segments.slice(0, segments.length - 2);
	}

	return {
		url: u.addTrailingSlash(u.join(parsedUrl.origin, ...segments)),
		specifier
	};
};
